<template>
    <div>
        <div class="flex flex-wrap">
            <div class="w-full lg:w-2/12">
                <router-link
                    to="/app/images"
                    class="rounded-full bg-brand-blue text-center p-2"
                >
                    <span>🏠</span>
                </router-link>
            </div>
            <div class="w-full lg:w-8/12 text-center">
                <h1 class="text-3xl">Subir imagen</h1>
            </div>
        </div>
        <div id="info-container" class="flex flex-wrap">
            <div class="w-7/12"></div>
            <div
                class="lg:w-5/12 w-full mt-2 flex items-center justify-between lg:justify-end"
            >
                <small>¿ Necesitas ayuda para crear las imágenes ?</small>
                <app-button
                    icon
                    border
                    :active="enableHelp"
                    @click="enableHelp = !enableHelp"
                >
                    ℹ️
                </app-button>
            </div>
        </div>
        <process-guide
            v-model="enableHelp"
            title="¿Cómo subir una imagen?"
            :steps="[
                'Selecciona el tipo de entidad y la entidad a la que quieres asociar la imagen.',
                'Haz clic en el área de carga y selecciona el archivo, verificando que tenga las dimensiones correctas.',
                'Opcionalmente ponle un título a la imagen: mejora su posicionamiento y facilita buscarla en el catálogo.',
                'Al completar la carga, la imagen se registra automáticamente en el sistema.'
            ]"
        >
            El nombre del archivo debe coincidir con el identificador de la
            entidad: para referencias, el código de barras; para categorías,
            colecciones, marcas, tiendas o bodegas, el slug.
        </process-guide>

        <div class="flex flex-wrap pb-4">
            <div class="w-full px-2 mt-2">
                <image-type-selector v-model="size" />
            </div>
        </div>

        <div class="flex">
            <div
                v-for="(item, index) in imagesRequired"
                :key="index"
                class="w-full md:w-1/2 lg:w-4/12 mx-auto"
            >
                <div class="w-full text-center">
                    <span v-if="item.strict">
                        Tamaño recomendado
                        <span class="text-theme-primary">
                            {{ item.width }} X {{ item.height }}
                        </span>
                    </span>
                    <span v-else class="text-theme-muted text-sm">
                        Esta imagen se sube tal cual, sin un tamaño o proporción
                        recomendada.
                    </span>
                </div>
                <div v-if="item.uploaded" class="w-full flex justify-center">
                    <img
                        loading="lazy"
                        :src="item.urlOriginal"
                        alt="Imagen subida"
                        class="w-64"
                    />
                </div>
                <div
                    v-else
                    class="w-full my-2 flex flex-col items-center gap-2"
                >
                    <label
                        class="w-64 flex flex-col items-center px-4 py-6 btn bg-theme-success cursor-pointer rounded-lg text-uppercase"
                    >
                        <img
                            src="@/assets/images/upload.svg"
                            height="20"
                            width="20"
                            alt="upload img"
                        />
                        <span class="mt-2 text-theme-base leading-normal">
                            Selecciona un archivo
                        </span>
                        <input
                            ref="uploader"
                            type="file"
                            :accept="ext"
                            :disabled="isLoading"
                            class="hidden"
                            @change="uploadButton($event, index)"
                        />
                    </label>
                    <image-rights-notice />
                </div>
            </div>
        </div>

        <div
            v-if="uploadedWithoutTitle"
            ref="titleWarning"
            class="flex flex-col items-center gap-3 mt-4"
        >
            <div
                class="w-full md:w-8/12 rounded-lg border border-theme-warning bg-theme-card px-4 py-3 text-theme-warning text-sm text-center"
            >
                ⚠️ La imagen fue subida sin título. Agrega uno para mejorar el
                posicionamiento del catálogo y buscar la imagen.
            </div>
            <div class="w-full md:w-8/12">
                <custom-input
                    v-model="title"
                    type="text"
                    name="title"
                    label="Título de la imagen"
                    placeholder="Ej: Camiseta azul talla M"
                    aria-label="Título de la imagen"
                    autocomplete="off"
                    :disabled="isLoading"
                    :error="
                        !validTitle && introValidators.title
                            ? 'Ingresa un título sin símbolos raros, entre 5 y 64 carácteres.'
                            : ''
                    "
                    @blur="introValidators.title = true"
                >
                    <template #icon> 📷</template>
                </custom-input>
            </div>
            <app-button
                gradient
                :disabled="!validTitle || isLoading"
                size="large"
                @click="updateTitle"
            >
                Agregar título
            </app-button>
        </div>

        <app-dialog
            v-model="showTitleModal"
            size="md"
            persistent
            @close="onTitleModalClosed"
        >
            <template #header>
                <h2 class="text-xl font-semibold">Título de la imagen</h2>
            </template>
            <p class="text-sm text-theme-muted mb-3">
                Ponle un título a la imagen para mejorar su posicionamiento y
                facilitar buscarla luego en el catálogo. Es opcional.
            </p>
            <custom-input
                v-model="title"
                type="text"
                name="title"
                label=""
                placeholder="Ej: Camiseta azul talla M"
                aria-label="Título de la imagen"
                autocomplete="off"
                :error="
                    !validTitle && introValidators.title
                        ? 'Ingresa un título sin símbolos raros, entre 5 y 64 carácteres.'
                        : ''
                "
                @blur="introValidators.title = true"
            >
                <template #icon> 📷</template>
            </custom-input>
            <template #footer>
                <div class="flex justify-center gap-2">
                    <app-button
                        gradient
                        :disabled="!!title && !validTitle"
                        @click="
                            titleModalConfirmed = true;
                            showTitleModal = false;
                        "
                    >
                        Continuar y subir imagen
                    </app-button>
                </div>
            </template>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { entitiesUploadImage } from "@/common/fields/entities-paths";
import { sizesImagesAll } from "@/common/fields/sizes-images";
import {
    aspectErrorMessage,
    validateAspectRatio
} from "@/common/lib/image-dimensions";
import {
    SVG_NOT_ALLOWED_MESSAGE,
    isAllowedImageFile
} from "@/common/lib/image-files";
import { altRegex } from "@/common/lib/regex";

import AppDialog from "@/components/utils/AppDialog.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImageRightsNotice from "@/components/utils/ImageRightsNotice.vue";
import ImageTypeSelector from "@/components/utils/ImageTypeSelector.vue";
import ProcessGuide from "@/components/utils/ProcessGuide.vue";

export default {
    name: "ImagesNew",
    components: {
        AppDialog,
        CustomInput,
        ImageRightsNotice,
        ImageTypeSelector,
        ProcessGuide
    },
    data: () => ({
        id: null,
        enableHelp: false,
        entity: "",
        ext: ".jpg,.jpeg,.png,.webp",
        title: "",
        url: "",
        size: "squareImage",
        isLoading: false,
        uploadedWithoutTitle: false,
        showTitleModal: false,
        titleModalConfirmed: false,
        pendingUploadIndex: null,
        file: { name: "", size: 0 },
        imagesRequired: [
            {
                size: "squareImage",
                url: "",
                urlOriginal: "",
                name: "",
                fileSize: 0,
                uploaded: false,
                height: 1200,
                width: 1200,
                strict: true
            }
        ],
        introValidators: { title: false },
        $entitiesUploadImage: entitiesUploadImage
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        validTitle() {
            return altRegex.test(this.title);
        }
    },
    watch: {
        size(value) {
            const aux = sizesImagesAll.find((item) => item.size === value);
            this.imagesRequired = aux.sizes.map((item) => ({
                ...item,
                url: "",
                name: "",
                fileSize: 0,
                uploaded: false
            }));
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("image:Create", "/app"))) return;
        const querySize = this.$route.query.size;
        if (sizesImagesAll.some((item) => item.size === querySize)) {
            this.size = querySize;
        }
    },
    methods: {
        ...mapActions("images", [
            "getEndPointUploadImage",
            "uploadImage",
            "createImage",
            "updateImage"
        ]),
        async uploadButton(event, index) {
            const auxFile = event.target.files || event.dataTransfer.files;
            if (!auxFile.length) return;
            this.file = auxFile[0];
            if (!isAllowedImageFile(this.file)) {
                this.clearInputAtIndex(index);
                return this.$swal.fire({
                    background: this.backgroundColor,
                    icon: "info",
                    title: "Formato no permitido",
                    text: SVG_NOT_ALLOWED_MESSAGE
                });
            }
            const fileSize = this.file.size / 1048576;
            if (fileSize > 20) {
                this.clearInputAtIndex(index);
                return this.$swal.fire({
                    icon: "info",
                    title: `Archivo es demasiado pesado: ${fileSize} MB`
                });
            }

            const aspect = await validateAspectRatio(
                this.file,
                this.imagesRequired[index]
            );
            if (!aspect.valid) {
                this.clearInputAtIndex(index);
                return this.$swal.fire({
                    background: this.backgroundColor,
                    icon: "info",
                    title: "La imagen no coincide con el tamaño",
                    text: aspectErrorMessage(aspect)
                });
            }

            if (!this.title) {
                this.pendingUploadIndex = index;
                this.showTitleModal = true;
                return;
            }

            await this.continueUpload(index);
        },
        onTitleModalClosed() {
            const index = this.pendingUploadIndex;
            const confirmed = this.titleModalConfirmed;
            this.pendingUploadIndex = null;
            this.titleModalConfirmed = false;
            if (!confirmed) {
                this.clearInputAtIndex(index);
                return;
            }
            this.continueUpload(index);
        },
        async continueUpload(index) {
            const fileTemp = {
                ...this.imagesRequired[index],
                name: this.file.name,
                fileSize: this.file.size,
                uploaded: false
            };

            this.isLoading = true;
            this.$loading.show();
            try {
                const { id, url, urlOriginal, urlSigned } =
                    await this.getEndPointUploadImage({
                        fileName: fileTemp.name,
                        fileSize: fileTemp.fileSize,
                        size: fileTemp.size,
                        alt: this.title,
                        entity: this.entity,
                        ...(this.id ? { id: this.id } : null)
                    });
                this.id = id;
                fileTemp.url = `${url}`;
                fileTemp.urlOriginal = `${urlOriginal}`;
                this.url = url.replace("original", "large");
                if (
                    fileTemp.size === "smallImage" ||
                    fileTemp.size === "normalImage"
                ) {
                    this.url = this.url
                        .replace("normal", "large")
                        .replace("small", "large");
                }
                await this.uploadImage({ url: urlSigned, file: this.file });
                fileTemp.uploaded = true;
                if (!this.title) {
                    this.uploadedWithoutTitle = true;
                    this.scrollToTitleWarning();
                }
                const imagesRequired = [...this.imagesRequired];
                imagesRequired[index] = { ...fileTemp };
                this.imagesRequired = [...imagesRequired];
                if (this.imagesRequired.every((item) => item.uploaded)) {
                    // createImage siempre cierra el cargue: el backend completa
                    // el alt faltante y marca la imagen como cargada con éxito
                    const result = await this.createImage({
                        id: this.id,
                        url: this.url,
                        alt: this.title,
                        size: this.size
                    });
                    this.$sound.play();
                    this.$loading.hide();
                    this.isLoading = false;
                    if (this.title) {
                        await this.showExitSwal(result.id);
                    }
                    return;
                }
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                fileTemp.uploaded = false;
                this.file = { name: "", size: 0 };
            }
        },
        async updateTitle() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateImage({
                    id: this.id,
                    alt: this.title,
                    size: this.size
                });
                this.$sound.play();
                this.$loading.hide();
                this.isLoading = false;
                this.uploadedWithoutTitle = false;
                await this.showExitSwal(this.id);
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async showExitSwal(imageId) {
            const { isConfirmed } = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Imagen registrada correctamente",
                icon: "success",
                showClass: {
                    popup: "animate__animated animate__fadeInDown"
                },
                hideClass: {
                    popup: "animate__animated animate__fadeOutUp"
                },
                showCancelButton: true,
                confirmButtonText: "Nueva carga",
                cancelButtonText: "Ver detalle",
                confirmButtonColor: "#02672D",
                cancelButtonColor: "#113ea2",
                allowOutsideClick: false,
                showCloseButton: true
            });
            if (!isConfirmed) {
                this.$router.push(`/app/images/detail/${imageId}`);
                return;
            }
            this.resetForm();
            this.resetUploadState();
        },
        resetUploadState() {
            this.clearInputLoader();
            this.id = null;
            const aux = sizesImagesAll.find((item) => item.size === this.size);
            this.imagesRequired = aux.sizes.map((item) => ({
                ...item,
                url: "",
                name: "",
                fileSize: 0,
                uploaded: false
            }));
        },
        resetForm() {
            this.clearInputLoader();
            this.id = null;
            this.id = null;
            this.title = "";
            this.introValidators = { title: false };
            this.file = { name: "", size: 0 };
            this.uploadedWithoutTitle = false;
            const aux = sizesImagesAll.find((item) => item.size === this.size);
            this.imagesRequired = aux.sizes.map((item) => ({
                ...item,
                url: "",
                name: "",
                fileSize: 0,
                uploaded: false
            }));
        },
        scrollToTitleWarning() {
            setTimeout(() => {
                if (this.$refs.titleWarning) {
                    this.$refs.titleWarning.scrollIntoView({
                        behavior: "smooth",
                        block: "end"
                    });
                }
            }, 500);
        },
        clearInputAtIndex(index) {
            if (this.$refs.uploader) {
                const uploaders = Array.isArray(this.$refs.uploader)
                    ? this.$refs.uploader
                    : [this.$refs.uploader];
                if (uploaders[index]) uploaders[index].value = null;
            }
        },
        clearInputLoader() {
            if (this.$refs.uploader) {
                const uploaders = Array.isArray(this.$refs.uploader)
                    ? this.$refs.uploader
                    : [this.$refs.uploader];
                uploaders.forEach((el) => {
                    if (el) el.value = null;
                });
            }
        }
    }
};
</script>
